<template>
  <app-collapse class="mt-1">
    <app-collapse-item
      :is-visible="false"
      class="collapsible-section-bg"
      title="Location of goods"
    >
      <template #header>
        <span
          class="lead collapse-title"
        >
          <section-status-icon
            :has-data="hasLocationOfGoodsData"
            :has-errors="hasSectionErrors"
          />
          Location of goods
        </span>
      </template>

      <!-- Loading state -->
      <b-row v-if="loadingWarehouses">
        <b-col class="text-center">
          <b-spinner label="Loading..." />
          <h6>
            Retrieving warehouses...
          </h6>
        </b-col>
      </b-row>

      <!-- Warehouse select (declaration-level field) -->
      <b-row v-if="declaration && !loadingWarehouses">
        <b-col
          cols="12"
          md="4"
        >
          <validation-provider
            v-slot="{ errors }"
            name="Warehouse"
            rules="required"
          >
            <b-form-group
              label="Choose warehouse"
              label-for="warehouse"
            >
              <b-form-select
                id="warehouse"
                v-model="declaration.warehouse_id"
                :options="warehouseOptions"
                :state="errors.length > 0 ? false : null"
                :disabled="disabled"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </b-form-group>
          </validation-provider>
        </b-col>
      </b-row>

      <!-- Readonly warehouse details -->
      <div v-if="selectedWarehouse">
        <hr class="section-separator">

        <!-- Location identification -->
        <b-row>
          <b-col
            cols="12"
            md="3"
          >
            <b-form-group>
              <label>
                Type of location
                <small class="text-muted">1615045000</small>
              </label>
              <div>{{ selectedWarehouse.type }}</div>
            </b-form-group>
          </b-col>

          <b-col
            cols="12"
            md="3"
          >
            <b-form-group>
              <label>
                Qualifier of identification
                <small class="text-muted">1615046000</small>
              </label>
              <div>{{ selectedWarehouse.qualifier }}</div>
            </b-form-group>
          </b-col>

          <b-col
            cols="12"
            md="3"
          >
            <b-form-group>
              <label>
                Identification of location
                <small class="text-muted">1615052000</small>
              </label>
              <div>{{ selectedWarehouse.code }}</div>
            </b-form-group>
          </b-col>

          <b-col
            cols="12"
            md="3"
          >
            <b-form-group>
              <label>
                Additional identifier
                <small class="text-muted">1615053000</small>
              </label>
              <div>{{ selectedWarehouse.additional_identifier }}</div>
            </b-form-group>
          </b-col>
        </b-row>

        <!-- Customs office & Address -->
        <b-row>
          <b-col
            cols="12"
            md="3"
          >
            <b-form-group>
              <label>
                Customs office
                <small class="text-muted">1615047001</small>
              </label>
              <div>{{ selectedWarehouse.customs_office }}</div>
            </b-form-group>
          </b-col>

          <b-col
            cols="12"
            md="3"
          >
            <b-form-group>
              <label>
                Street and number
                <small class="text-muted">1615018019</small>
              </label>
              <div>{{ selectedWarehouse.street_and_number }}</div>
            </b-form-group>

            <b-form-group>
              <label>
                Postcode
                <small class="text-muted">1615018021</small>
              </label>
              <div>{{ selectedWarehouse.postal_code }}</div>
            </b-form-group>
          </b-col>

          <b-col
            cols="12"
            md="3"
          >
            <b-form-group>
              <label>
                City
                <small class="text-muted">1615018022</small>
              </label>
              <div>{{ selectedWarehouse.city }}</div>
            </b-form-group>
          </b-col>

          <b-col
            cols="12"
            md="3"
          >
            <b-form-group>
              <label>
                Country
              </label>
              <div>{{ selectedWarehouse.country }}</div>
            </b-form-group>
          </b-col>
        </b-row>
      </div>
    </app-collapse-item>
  </app-collapse>
</template>

<script>
import {
  BRow,
  BCol,
  BFormGroup,
  BFormSelect,
  BSpinner,
} from 'bootstrap-vue'
import { ValidationProvider } from 'vee-validate'
import AppCollapse from '@core/components/app-collapse/AppCollapse.vue'
import AppCollapseItem from '@core/components/app-collapse/AppCollapseItem.vue'
import errorHighlightingMixin from '@/mixins/errorHighlightingMixin'
import SectionStatusIcon from '@/views/shipments/declarations/modals/components/h1/SectionStatusIcon.vue'

export default {
  components: {
    BRow,
    BCol,
    BFormGroup,
    BFormSelect,
    BSpinner,
    ValidationProvider,
    AppCollapse,
    AppCollapseItem,
    SectionStatusIcon,
  },
  mixins: [errorHighlightingMixin],
  props: {
    parcel: {
      type: Object,
      required: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    countriesOptions: {
      type: Array,
      default: () => [],
    },
    declaration: {
      type: Object,
      default: null,
    },
    companyId: {
      type: Number,
      default: null,
    },
  },
  data() {
    return {
      warehouseList: [],
      loadingWarehouses: false,
    }
  },
  computed: {
    warehouseOptions() {
      const options = [{ value: '', text: '----select----' }]
      return options.concat(
        this.warehouseList.map(w => ({ value: w.id, text: w.name })),
      )
    },
    selectedWarehouse() {
      if (!this.declaration || !this.declaration.warehouse_id) {
        return null
      }
      return this.warehouseList.find(w => w.id === this.declaration.warehouse_id) || null
    },
    hasLocationOfGoodsData() {
      return !!(this.declaration && this.declaration.warehouse_id)
    },
    hasSectionErrors() {
      return Object.keys(this.issuesMap).some(key => key.startsWith('location_of_goods.'))
    },
  },
  created() {
    if (this.companyId) {
      this.loadingWarehouses = true
      this.$http.get(`/v1/companies/${this.companyId}/warehouses`)
        .then(response => {
          this.warehouseList = response.data.data
        })
        .finally(() => {
          this.loadingWarehouses = false
        })
    }
  },
}
</script>

<style scoped lang="scss">
@import '@core/scss/base/bootstrap-extended/variables';
@import '@core/scss/base/core/mixins/declarations-mixins';

.collapsible-section-bg {
  @include collapsible-section-bg;
}

.input-warning {
  @include input-warning;
}

.input-danger {
  @include input-danger;
}

.section-separator {
  margin: 1.5rem 0;
  border-top: 2px solid #d8dce0;
}
</style>
